import { useState, useEffect, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, HttpError } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface ContactRow {
  id: string;
  display_name: string | null;
  primary_phone: string | null;
}

interface PropertyRow {
  id: string;
  name: string;
  area: string;
  status: string;
  monthly_price_idr: number | null;
}

export function CreateVisitModal({ open, onClose, onCreated }: Props) {
  const [contactId, setContactId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [conversationId, setConversationId] = useState("");
  const [scheduledStart, setScheduledStart] = useState("");
  const [visitType, setVisitType] = useState<"physical" | "virtual">("physical");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Reset form on open
  useEffect(() => {
    if (open) {
      setContactId("");
      setPropertyId("");
      setConversationId("");
      // Default to tomorrow 10:00 local
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(10, 0, 0, 0);
      setScheduledStart(d.toISOString().slice(0, 16));
      setVisitType("physical");
      setNotes("");
      setError(null);
    }
  }, [open]);

  const { data: contacts = [] } = useQuery({
    queryKey: ["pickers", "contacts"],
    queryFn: () => api.get<ContactRow[]>("/api/contacts?limit=200"),
    enabled: open,
  });
  const { data: properties = [] } = useQuery({
    queryKey: ["pickers", "properties"],
    queryFn: () => api.get<PropertyRow[]>("/api/properties?status=available&limit=200"),
    enabled: open,
  });

  const createMut = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/api/visits", {
        contact_id: contactId,
        property_id: propertyId,
        conversation_id: conversationId || undefined,
        scheduled_start: new Date(scheduledStart).toISOString(),
        visit_type: visitType,
        notes: notes || undefined,
      }),
    onSuccess: () => onCreated(),
    onError: (err) => {
      if (err instanceof HttpError) {
        const body = err.body as { message?: string; error?: string } | null;
        setError(body?.message || body?.error || `HTTP ${err.status}`);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
  });

  if (!open) return null;

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!contactId || !propertyId || !scheduledStart) {
      setError("Contact, property and scheduled_start are required.");
      return;
    }
    createMut.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg bg-brand-surface rounded-lg shadow-xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="font-semibold">New visit</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </header>
        <form onSubmit={submit} className="px-5 py-4 space-y-3 text-sm">
          <Field label="Contact">
            <select
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm"
            >
              <option value="">-- pick a contact --</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name || "(no name)"} {c.primary_phone ? `· ${c.primary_phone}` : ""}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Property (available only)">
            <select
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm"
            >
              <option value="">-- pick a property --</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.area}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Scheduled start (local)">
            <Input
              type="datetime-local"
              value={scheduledStart}
              onChange={(e) => setScheduledStart(e.target.value)}
            />
          </Field>

          <Field label="Visit type">
            <div className="flex gap-2">
              {(["physical", "virtual"] as const).map((t) => (
                <Button
                  key={t}
                  type="button"
                  size="sm"
                  variant={visitType === t ? "primary" : "outline"}
                  onClick={() => setVisitType(t)}
                >
                  {t}
                </Button>
              ))}
            </div>
          </Field>

          <Field label="Conversation ID (optional)">
            <Input
              value={conversationId}
              onChange={(e) => setConversationId(e.target.value)}
              placeholder="for the auto-confirmation message"
            />
          </Field>

          <Field label="Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </Field>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create visit
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
